import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  ChevronUp,
  Edit2,
  Eye,
  EyeOff,
  RefreshCw,
  Search,
  Trash2,
  PlusCircle,
  XCircle,
} from "feather-icons-react";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader } from "../../core/redux/action";
import Table from "../../core/pagination/datatable";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";
import Loader from "../loader/loader";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const CreateBusinesses = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [formData, setFormData] = useState({
    business_id: undefined,
    business_name: "",
    name: "",
    email: "",
    phone: "",
    status: "active",
    password: "",
    confirm_password: "",
    subscription_id: null,
    bank_name: "",
    amount: undefined,
    expiry_date: "",
    subscription_expiry: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [currentTab, setCurrentTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [renewMode, setRenewMode] = useState(false);
  const [plans, setPlans] = useState([]);

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get("/get-plans");
      if (response.success && Array.isArray(response.data)) {
        setPlans(response.data);
      } else {
        showToast("Failed to fetch plans", "error", "Plan Fetch");
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch plans",
        "error",
        "Plan Fetch"
      );
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (renewMode) {
      if (!formData.business_id)
        newErrors.business_id = "Business ID is required";
      if (!formData.business_name?.trim())
        newErrors.business_name = "Business Name is required";
      if (formData.amount === undefined || formData.amount < 0)
        newErrors.amount = "Amount must be a non-negative number";
      if (!formData.expiry_date)
        newErrors.expiry_date = "Expiry Date is required";
      else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiryDate = new Date(formData.expiry_date);
        if (isNaN(expiryDate) || expiryDate < today) {
          newErrors.expiry_date = "Expiry Date must be today or in the future";
        }
      }
    } else {
      if (!formData.name?.trim()) newErrors.name = "Business Name is required";
      if (!formData.email?.trim()) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.email))
        newErrors.email = "Invalid email format";
      if (!formData.subscription_expiry)
        newErrors.subscription_expiry = "Expiry Date is required";
      else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiryDate = new Date(formData.subscription_expiry);
        if (isNaN(expiryDate) || expiryDate < today) {
          newErrors.subscription_expiry =
            "Expiry Date must be today or in the future";
        }
      }
      if (!isEditing) {
        if (!formData.password) newErrors.password = "Password is required";
        if (!formData.confirm_password)
          newErrors.confirm_password = "Confirm Password is required";
        else if (formData.password !== formData.confirm_password) {
          newErrors.confirm_password = "Passwords do not match";
        }
      }
    }
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "subscription_id"
          ? value
            ? Number(value)
            : null
          : name === "amount"
          ? Number(value)
          : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();

    if (isEditing) {
      delete validationErrors.password;
      delete validationErrors.confirm_password;
    }

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      try {
        const apiPayload = renewMode
          ? {
              business_id: formData.business_id,
              business_name: formData.business_name,
              subscription_id: formData.subscription_id,
              bank_name: formData.bank_name,
              amount: formData.amount,
              expiry_date: formData.expiry_date,
            }
          : {
              id: editId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              status: formData.status,
              subscription_expiry: formData.subscription_expiry,
              ...(isEditing && !formData.password
                ? {}
                : { password: formData.password }),
            };

        const endpoint = renewMode
          ? "/transaction-history"
          : "/store-update-business";
        const response = await apiClient.post(endpoint, apiPayload);

        if (response.success) {
          setFormData({
            business_id: undefined,
            business_name: "",
            name: "",
            email: "",
            phone: "",
            status: "active",
            password: "",
            confirm_password: "",
            subscription_id: null,
            bank_name: "",
            amount: undefined,
            expiry_date: "",
            subscription_expiry: "",
          });
          setIsEditing(false);
          setEditId(null);
          setRenewMode(false);
          hasFetched.current = false;
          setErrors({});
          setShowModal(false);
          showToast(response.message, "success", "Business Operation");
          await fetchBusiness();
        } else {
          setErrors(response.errors || {});
          showToast(
            response.message || "Operation failed",
            "error",
            "Business Operation"
          );
        }
      } catch (error) {
        showToast(
          error.message || "Operation failed",
          "error",
          "Business Operation"
        );
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const handleAdd = () => {
    setFormData({
      business_id: undefined,
      business_name: "",
      name: "",
      email: "",
      phone: "",
      status: "active",
      password: "",
      confirm_password: "",
      subscription_id: null,
      bank_name: "",
      amount: undefined,
      expiry_date: "",
      subscription_expiry: "",
    });
    setErrors({});
    setIsEditing(false);
    setEditId(null);
    setRenewMode(false);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setFormData({
      name: record.name || "",
      email: record.email || "",
      phone: record.phone || "",
      status: record.status,
      password: "",
      confirm_password: "",
      subscription_expiry: record.subscription_expiry || "",
    });
    setIsEditing(true);
    setEditId(record.id);
    setRenewMode(false);
    setErrors({});
    setShowModal(true);
  };

  const handleRenew = async (record) => {
    setFormData({
      business_id: record.id,
      business_name: record.name,
      email: record.email || "",
      phone: record.phone || "",
      status: "active",
      password: "",
      confirm_password: "",
      subscription_id: null,
      bank_name: "",
      amount: undefined,
      expiry_date: "",
    });
    setIsEditing(false);
    setEditId(record.id);
    setRenewMode(true);
    setErrors({});
    await fetchPlans();
    setShowModal(true);
  };

  const handleToggleStatus = async (record) => {
    try {
      const response = await apiClient.post("/store-update-business", {
        id: record.id,
        status: record.status === "active" ? "inactive" : "active",
        name: record.name,
        email: record.email,
        phone: record.phone,
        subscription_expiry: record.subscription_expiry,
      });
      if (response.success) {
        showToast(response.message, "success", "Business Operation");
        hasFetched.current = false;
        await fetchBusiness();
      } else {
        showToast(
          response.message || "Operation failed",
          "error",
          "Business Operation"
        );
      }
    } catch (error) {
      showToast(
        error.message || "Operation failed",
        "error",
        "Business Operation"
      );
    }
  };

  const handleCancelSubscription = (record) => {
    const MySwal = withReactContent(Swal);
    MySwal.fire({
      title: "Cancel Subscription?",
      text: `Are you sure you want to cancel the subscription for ${record.name}?`,
      showCancelButton: true,
      confirmButtonColor: "#ff0000",
      confirmButtonText: "Confirm",
      cancelButtonColor: "#6c757d",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const response = await apiClient.post("/cancel-subscription", {
            business_id: record.id,
          });
          if (response.status) {
            showToast(
              "Subscription cancelled successfully",
              "success",
              "Business Operation"
            );
            hasFetched.current = false;
            await fetchBusiness();
          } else {
            showToast(
              response.message || "Failed to cancel subscription",
              "error",
              "Business Operation"
            );
          }
        } catch (error) {
          showToast(
            error.message || "Failed to cancel subscription",
            "error",
            "Business Operation"
          );
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const fetchBusiness = async () => {
    if (hasFetched.current) return;
    setLoading(true);
    try {
      const response = await apiClient.post("/get-all-business", {
        pageIndex: 0,
        pageSize: 50,
        previousPageIndex: 0,
      });
      if (response.success && Array.isArray(response.data)) {
        setBusinesses(response.data);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const filtered =
          currentTab === "expired"
            ? response.data.filter((business) => {
                const isExpired =
                  !business.subscription_expiry ||
                  new Date(business.subscription_expiry) < today;
                return isExpired;
              })
            : response.data;
        setFilteredBusinesses(filtered);
      } else {
        setBusinesses([]);
        setFilteredBusinesses([]);
        showToast(
          response.message || "Failed to fetch businesses",
          "error",
          "Business Operation"
        );
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch businesses",
        "error",
        "Business Operation"
      );
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };

  const handleTabChange = (tab) => {
    setCurrentTab(tab);
    setSearchTerm("");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filtered =
      tab === "expired"
        ? businesses.filter((business) => {
            const isExpired =
              !business.subscription_expiry ||
              new Date(business.subscription_expiry) < today;
            return isExpired;
          })
        : businesses;
    setFilteredBusinesses(filtered);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filtered = businesses.filter((business) => {
      const matchesSearch =
        (business.name || "").toLowerCase().includes(term) ||
        (business.status || "").toLowerCase().includes(term) ||
        (business.email || "").toLowerCase().includes(term) ||
        (business.phone || "").toLowerCase().includes(term);
      if (currentTab === "expired") {
        const isExpired =
          !business.subscription_expiry ||
          new Date(business.subscription_expiry) < today;
        return matchesSearch && isExpired;
      }
      return matchesSearch;
    });
    setFilteredBusinesses(filtered);
  };

  const showConfirmationAlert = (record) => {
    const MySwal = withReactContent(Swal);
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const response = await apiClient.post("/delete-business", {
            id: record.id,
          });
          if (response.success) {
            showToast(
              "Business deleted successfully",
              "success",
              "Business Operation"
            );
            hasFetched.current = false;
            await fetchBusiness();
          } else {
            showToast(
              response.message || "Failed to delete business",
              "error",
              "Business Operation"
            );
          }
        } catch (error) {
          showToast(
            error.message || "Failed to delete business",
            "error",
            "Business Operation"
          );
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: "Business Name",
      dataIndex: "name",
      render: (text) => <span className="text-wrap">{text}</span>,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      width: 200,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: (a, b) => (a.phone || "").localeCompare(b.phone || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      render: (text) => <span className="text-wrap">{text}</span>,
      width: 300,
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (text) => {
        const date = new Date(text);
        return isNaN(date.getTime())
          ? "N/A"
          : date
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
              .split("/")
              .join("-");
      },
      width: 100,
    },
    {
      title: "Subscription Expiry Date",
      dataIndex: "subscription_expiry",
      sorter: (a, b) => {
        const dateA = a.subscription_expiry
          ? new Date(a.subscription_expiry).getTime()
          : 0;
        const dateB = b.subscription_expiry
          ? new Date(b.subscription_expiry).getTime()
          : 0;
        return dateA - dateB;
      },
      render: (text) => {
        if (!text) return "N/A"; // Check for null or undefined
        const date = new Date(text);
        return isNaN(date.getTime())
          ? "N/A"
          : date
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
              .split("/")
              .join("-");
      },
      width: 100,
    },
    // {
    //     title: 'Status',
    //     dataIndex: 'status',
    //     render: (text) => (
    //         <span className={`badge ${text === 'active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
    //             {text === 'active' ? 'Active' : 'Inactive'}
    //         </span>
    //     ),
    //     sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
    //     width: 100,
    // },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => (
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={text === "active"}
            onChange={() => handleToggleStatus(record)}
          />
          <span className="toggle-slider"></span>
        </label>
      ),
      sorter: (a, b) => a.status.length - b.status.length,
      width: 100,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="edit-delete-action">
          {currentTab !== "expired" && (
            <button
              className="me-2 p-2 d-flex align-items-center border rounded"
              onClick={() => handleEdit(record)}
              aria-label="Edit business"
            >
              <Edit2 size={16} />
            </button>
          )}
          {currentTab === "expired" && (
            <button
              className="me-2 p-2 renew-btn border rounded"
              onClick={() => handleRenew(record)}
              title="Renew Business"
              aria-label="Renew business"
            >
              <RefreshCw size={16} />
            </button>
          )}
          <button
            className="me-2 p-2 d-flex align-items-center border rounded text-red"
            onClick={() => showConfirmationAlert(record)}
            aria-label="Delete business"
          >
            <Trash2 size={16} />
          </button>

          {currentTab !== "expired" && (
            <button
              className="me-2 p-2 cancel-btn border rounded"
              onClick={() => handleCancelSubscription(record)}
              title="Cancel Subscription"
              aria-label="Cancel subscription"
            >
              <XCircle size={16} />
            </button>
          )}
        </div>
      ),
      width: 100,
    },
  ];

  useEffect(() => {
    fetchBusiness();
  }, []);

  return (
    <div>
      <style>
        {`
          .nav-tabs {
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            gap: 1.5rem;
            padding-bottom: 0;
          }
          .nav-item {
            margin-bottom: 0px !important;
          }
          .nav-link {
            border: none !important;
            border-bottom: 2px solid transparent;
            background: none;
            color: #6b7280;
            font-weight: 500;
            padding: 0.5rem 1rem;
            transition: color 0.2s ease;
          }
          .nav-link:hover {
            color: #374151;
          }
          .nav-link.active {
            border-bottom: 5px solid #f59e0b !important;
            color: #1f2937 !important;
            font-weight: 600 !important;
          }
          .renew-btn:hover {
            color: #28a745 !important;
          }
        `}
      </style>
      <Loader loading={loading} />
      <div
        className="page-wrapper"
        style={{ position: "relative", minHeight: "100vh" }}
      >
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Business List</h4>
                <h6>Manage Your Businesses</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={(props) => <Tooltip {...props}>Collapse</Tooltip>}
                >
                  <button
                    className={`me-2 d-flex align-items-center border rounded ${
                      data ? "active" : ""
                    }`}
                    onClick={() => dispatch(setToogleHeader(!data))}
                    aria-label="Toggle header"
                  >
                    <ChevronUp />
                  </button>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="page-btn">
              <button
                className="btn btn-added"
                onClick={handleAdd}
                aria-label="Add new business"
              >
                <PlusCircle className="me-2" />
                Add New Business
              </button>
            </div>
          </div>
          <div className="card table-list-card">
            <div
              className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
              style={{ padding: "1rem 1.25rem 0" }}
            >
              <div className="tabs-menu">
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        currentTab === "all" ? "active" : ""
                      } px-4 py-2`}
                      onClick={() => handleTabChange("all")}
                    >
                      All Businesses
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        currentTab === "expired" ? "active" : ""
                      } px-4 py-2`}
                      onClick={() => handleTabChange("expired")}
                    >
                      Expired Businesses
                    </button>
                  </li>
                </ul>
              </div>
              <div className="search-set mb-1">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                    value={searchTerm}
                    onChange={handleSearch}
                    aria-label="Search businesses"
                  />
                  <button className="btn btn-searchset" aria-label="Search">
                    <Search size={16} />
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body pb-0">
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={filteredBusinesses}
                  rowSelection={null}
                  rowKey={(record) => record.id}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {renewMode
              ? "Renew Business"
              : isEditing
              ? "Edit Business"
              : "Add Business"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit} id="business-form">
            <div className="row">
              {renewMode ? (
                <>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Business Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.business_name ? "is-invalid" : ""
                        }`}
                        name="business_name"
                        value={formData.business_name || ""}
                        disabled
                      />
                      {errors.business_name && (
                        <div className="invalid-feedback">
                          {errors.business_name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Subscription Plan</label>
                      <select
                        className="form-select"
                        name="subscription_id"
                        value={formData.subscription_id || ""}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Plan</option>
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Bank Name (Optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        name="bank_name"
                        value={formData.bank_name || ""}
                        onChange={handleInputChange}
                        placeholder="Enter Bank Name"
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Amount</label>
                      <input
                        type="number"
                        className={`form-control ${
                          errors.amount ? "is-invalid" : ""
                        } no-spinner`}
                        name="amount"
                        value={
                          formData.amount !== undefined ? formData.amount : ""
                        }
                        onChange={handleInputChange}
                        placeholder="Enter Amount"
                        min="0"
                        step="0.01"
                        onWheel={(e) => e.target.blur()}
                      />
                      {errors.amount && (
                        <div className="invalid-feedback">{errors.amount}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className={`form-control ${
                          errors.expiry_date ? "is-invalid" : ""
                        }`}
                        name="expiry_date"
                        value={formData.expiry_date || ""}
                        onChange={handleInputChange}
                      />
                      {errors.expiry_date && (
                        <div className="invalid-feedback">
                          {errors.expiry_date}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Business Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder="Enter Business Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={isEditing}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className={`form-control ${
                          errors.email ? "is-invalid" : ""
                        }`}
                        placeholder="Enter Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isEditing}
                      />
                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="number"
                        className={`form-control ${
                          errors.phone ? "is-invalid" : ""
                        } no-spinner`}
                        placeholder="Enter Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        onWheel={(e) => e.target.blur()}
                      />
                      {errors.phone && (
                        <div className="invalid-feedback">{errors.phone}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className={`form-control ${
                          errors.subscription_expiry ? "is-invalid" : ""
                        }`}
                        name="subscription_expiry"
                        value={formData.subscription_expiry}
                        onChange={handleInputChange}
                      />
                      {errors.subscription_expiry && (
                        <div className="invalid-feedback">
                          {errors.subscription_expiry}
                        </div>
                      )}
                    </div>
                  </div>
                  {!isEditing && (
                    <>
                      <div className="col-lg-6">
                        <div className="mb-3 position-relative">
                          <label className="form-label">Password</label>
                          <input
                            type={showPassword ? "text" : "password"}
                            className={`form-control ${
                              errors.password ? "is-invalid" : ""
                            }`}
                            placeholder="Enter Password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                          />
                          <span
                            className="position-absolute"
                            style={{
                              right: "10px",
                              top: "38px",
                              cursor: "pointer",
                            }}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <Eye size={20} />
                            ) : (
                              <EyeOff size={20} />
                            )}
                          </span>
                          {errors.password && (
                            <div className="invalid-feedback">
                              {errors.password}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3 position-relative">
                          <label className="form-label">Confirm Password</label>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className={`form-control ${
                              errors.confirm_password ? "is-invalid" : ""
                            }`}
                            placeholder="Enter Confirm Password"
                            name="confirm_password"
                            value={formData.confirm_password}
                            onChange={handleInputChange}
                          />
                          <span
                            className="position-absolute"
                            style={{
                              right: "10px",
                              top: "38px",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <Eye size={20} />
                            ) : (
                              <EyeOff size={20} />
                            )}
                          </span>
                          {errors.confirm_password && (
                            <div className="invalid-feedback">
                              {errors.confirm_password}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer-btn">
              <Button
                className="me-2"
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                form="business-form"
                disabled={loading}
              >
                {loading
                  ? "Processing..."
                  : renewMode
                  ? "Renew"
                  : isEditing
                  ? "Update"
                  : "Submit"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

CreateBusinesses.propTypes = {};

export default CreateBusinesses;
